import IconButton from '@/components/UI/buttons/IconButton'
import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import SummaryItem from '@/features/shiftCloseouts/components/CashTransactionsHeader/parts/SummaryItem'
import { CashTransactionTotals } from '@/features/shiftCloseouts/utils/calculateCashTransactionTotals'
import { formatSignedPrice } from '@/features/shiftCloseouts/utils/formatSignedPrice'
import BackArrowIcon from '@/features/ui/icons/components/BackArrowIcon'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CashTransactionsHeaderProps {
    layout: Layout
    onBack: () => void
    totals: CashTransactionTotals
}

const CashTransactionsHeader: FC<CashTransactionsHeaderProps> = ({ layout, onBack, totals }) => {
    const { t } = useTranslation()
    const { styles: responsiveStyles } = useStyles(stylesheet, { layout })

    return (
        <View style={responsiveStyles.container} testID="cash-transactions-header">
            <View style={responsiveStyles.titleRow}>
                <Typography.Heading
                    variant={layout === 'compact' ? 'h2' : 'h1'}
                    style={responsiveStyles.title}
                >
                    {t('shiftCloseouts.cashTransactions.title')}
                </Typography.Heading>

                {layout === 'extended' && (
                    <IconButton
                        accessibilityLabel={t('actions.back')}
                        Icon={() => <BackArrowIcon color={ColorEnum.GRAY_900} />}
                        onPress={onBack}
                    />
                )}
            </View>

            <View style={responsiveStyles.summary} testID="cash-transactions-header-summary">
                <SummaryItem
                    color={ColorEnum.EMERALD_700}
                    label={t('shiftCloseouts.cashTransactions.summary.deposits')}
                    layout={layout}
                    testID="cash-transactions-header-deposits"
                    value={formatSignedPrice(
                        totals.deposits.amount,
                        totals.deposits.currency,
                        'positive',
                        2
                    )}
                />
                <SummaryItem
                    color={ColorEnum.BRICK_500}
                    label={t('shiftCloseouts.cashTransactions.summary.withdrawals')}
                    layout={layout}
                    testID="cash-transactions-header-withdrawals"
                    value={formatSignedPrice(
                        totals.withdrawals.amount,
                        totals.withdrawals.currency,
                        'negative',
                        2
                    )}
                />
                <SummaryItem
                    color={layout === 'compact' ? ColorEnum.GRAY_900 : ColorEnum.GRAY_600}
                    emphasized
                    label={t('shiftCloseouts.cashTransactions.summary.difference')}
                    layout={layout}
                    testID="cash-transactions-header-difference"
                    value={formatSignedPrice(
                        totals.total.amount,
                        totals.total.currency,
                        layout === 'compact' ? 'negative-only' : 'dynamic',
                        2
                    )}
                />
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    container: {
        gap: 8,
        padding: 16,
        paddingLeft: 16 + insets.left,
        paddingRight: 16 + insets.right,
        borderBottomColor: ColorEnum.GRAY_200,
        borderBottomWidth: 1,
        variants: {
            layout: {
                compact: {
                    gap: 16,
                },
                extended: {},
            },
        },
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    title: {
        flexShrink: 1,
        color: ColorEnum.GRAY_900,
    },
    summary: {
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                    gap: 4,
                },
                extended: {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 16,
                },
            },
        },
    },
}))

export default CashTransactionsHeader
